import * as React from "react";

import { useAuth } from "@/contexts/auth-context";
import { apiFetch } from "@/lib/api-client";
import {
  LEGACY_VERIFICATION_PENDING_REASON,
  ORG_LAPSE_CONTACT_ADMIN,
  isLegacyOnboardingPillState,
  selectSubscriptionPillState,
  subscriptionPill,
  type LegacyOnboardingState,
} from "@/lib/subscription-copy";
import { USER_ORIGIN_WEBAPP_LEGACY } from "@/lib/user-origin";
import { Badge } from "@/components/ui/badge";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  isPreCalibrationNotes,
  isResolvedSubscription,
  useSubscriptionState,
  useSubscriptionStateError,
  type SubscriptionState,
} from "@/hooks/queries/useSubscriptionState";
import { CLINICIAN_ROLES } from "@/routes/route-access";
import { toCheckoutErrorMessage } from "./checkout-error";

// Story 10.8 — the Profile "Subscription" section (Screen 14 §13.12). Renders ONE
// of three billing variants selected by DATA, never role: `source === "org_seat"`
// → B2B helper copy (no CTAs); otherwise B2C split on the JWT `user_origin`.
//
// Story 10.15 (FR48 inversion) ended the B2C split's asymmetry: BOTH B2C variants
// now drive the same in-product Stripe checkout. `webapp_legacy` has since 10.7;
// the LearnWorlds cohort's year-2 renewal moved in-product when 10.14 opened
// `POST /billing/checkout-sessions/create` to it, retiring the gh-643 interim
// apparatus (`SHOW_LEARNWORLDS_RENEW_CTA`, `LEARNWORLDS_ACCOUNT_URL`) that had
// left this variant rendering nothing at all. They keep separate visibility
// gates — the legacy path has no renewal window, the LearnWorlds one does — and
// share the button itself. Plus a three-state status pill (active /
// pending-calibration / lapsed) + a renewal date. Card/Badge/Button + tokens only
// (mirrors LegacyCertCard on this same screen).

const B2B_HELPER_COPY =
  "Your subscription is provided by your organization's CITS contract. Contact " +
  "your organization administrator for renewal or seat changes.";

// Story 13.6 (UX 13.11) — the read-only tail of the org-lapsed helper copy;
// the "ended on {date}" lead sentence is prepended only when a lapse instant
// exists (an inactive org seat with a NULL valid_until — pre-start / data
// drift — is not lapsed and gets no date sentence).
const B2B_READ_ONLY_COPY =
  `You retain read-only access to historical records. ${ORG_LAPSE_CONTACT_ADMIN}`;

const RENEWAL_WINDOW_DAYS = 30;
const RENEWAL_WINDOW_MS = RENEWAL_WINDOW_DAYS * 24 * 60 * 60 * 1000;

// gh-641 (FR47) — a paid `pending_calibration` account has nothing to renew.
// The sentence is UX spec §13.11 verbatim (Story 16.6 AC10): no mockup renders
// this moment, so the spec text is the contract, and Screen 14b's "Locked until
// verification" copy describes a DIFFERENT moment (cert not yet approved), which
// is why it is not reused here — it is rendered by 14b's OWN branch below.
// The server side already refuses a second checkout (409 BILL-005 —
// LIVE_SUBSCRIPTION_STATUSES includes `pending_calibration`); this keeps the UI
// from offering it.
const LEGACY_PENDING_CALIBRATION_COPY =
  "Your subscription will begin once you complete calibration.";

// ---------------------------------------------------------------------------
// Screen 14b — the STAGE-1 card (certificate uploaded, CITS review pending).
// Client-validated, and until now unimplemented: this clinician resolved the
// `no_entitlement` default and was handed the generic B2C lapse pill above an
// ENABLED "Renew subscription", which `POST /billing/checkout-sessions/create`
// answers 403 LEGACY-001 — FR39 puts payment AFTER approval.
//
// The copy is 14b verbatim: the body from mockup line 150, the CTA label from
// line 153. The pill text and the disabled reason live in
// `lib/subscription-copy.ts` — the pill because every pill in the product does,
// the reason because 14b puts the identical sentence on the readiness card's
// locked CTA one section above this one.
// ---------------------------------------------------------------------------

const LEGACY_PENDING_VERIFICATION_COPY =
  "Stripe checkout will appear here once your certificate is verified. No " +
  "payment is taken before then.";

const LEGACY_PENDING_VERIFICATION_CTA = "Pay annual subscription";

// ---------------------------------------------------------------------------
// Story 16.7 — the legacy ONBOARDING states (W3 field contract, Screen 14).
//
// These are the moments between "CITS verified my certificate" and "my account
// is enabled", and they are distinguished from every state above by the JWT
// (`isLegacyGated`), never by the billing payload alone. The payload cannot tell
// them apart on its own: `entitlements._no_entitlement` returns
// `source="individual_subscription"` for a clinician with NO subscription row at
// all, so a bare `source` read cannot answer "have they paid?". `notes` can —
// `"Pre-calibration"` is stamped only when a row exists
// (`individual_subscription_provider.py:72-80`), `"no_entitlement"` only when
// none does.
//
// The first two rows' copy is verbatim from the `epics.md` AC. The last two have
// NO client-validated source — no mockup renders them — so they are pinned here,
// pinned again in AC13/AC14, and flagged `[→human:PM]` for ratification.
// Screen 14b's "Locked until verification" copy describes 16.4's moment (the
// certificate is still under review) and is not reused for any of these four —
// 16.7's W3 decision row 15 rejected exactly that, and rightly. It is rendered
// by the stage-1 branch above instead, which IS 14b's moment.
// ---------------------------------------------------------------------------

const LEGACY_AWAITING_PAYMENT_COPY = "Complete payment to access calibration.";

const LEGACY_ACTIVATING_COPY =
  "Payment received. Your account is being enabled — this normally takes less " +
  "than a minute.";

// AC14. Deliberately a SENTENCE and not a link: no support address exists
// anywhere in this codebase (the one precedent, `BeginCalibrationScreen.tsx`,
// also names the channel in prose), and inventing a URL on a payment-failure
// surface is the kind of value that must come from the client, not from here.
const LEGACY_ACTIVATION_SUPPORT_COPY =
  "This is taking longer than usual. Contact CITS support if your account is " +
  "still locked.";

/** How long the waiting state may persist before the support prompt joins it (AC14). */
export const ACTIVATION_SUPPORT_PROMPT_MS = 2 * 60 * 1000;

// The onboarding states, their pills (both `pending` — the W3 table says so for
// all four rows) and the discriminator that picks them now live in
// `lib/subscription-copy.ts`, together with the billing states. `AccessReadinessCard`
// re-derived that split WITHOUT the `certVerified` term this section carried, so
// the two renderers on the same page could disagree (DW-458); one home is the fix.

/**
 * AC14 — has the waiting state outlived the threshold? A pure elapsed-time
 * question, so the surface never counts retries or polls (a retry count would
 * make the prompt depend on how often the tab happened to be focused).
 *
 * `activatingSince` is the SERVER's instant — `claimCreatedAt`, the creation time
 * of the subscription row the webhook wrote, which for the legacy checkout IS
 * the payment instant. It has to come from the server: an anchor started when
 * the SPA first observes the waiting state restarts on every reload, so the one
 * population AC14 exists for — a clinician whose activation is genuinely stuck,
 * who reloads Profile precisely because nothing is happening — would never reach
 * the prompt at all.
 *
 * `null` means the resolving claim has no such row (`org_seat` /
 * `manual_override`) and therefore no payment instant to measure from. It is
 * read as UNKNOWN, never as "just now" and never as "long ago": returning
 * `false` withholds the prompt rather than firing it for everyone undatable.
 */
export function isSupportPromptDue(
  activatingSince: number | null,
  now: number,
): boolean {
  if (activatingSince === null) return false;
  return now - activatingSince >= ACTIVATION_SUPPORT_PROMPT_MS;
}

// Story 10.15 (PM decision 2026-08-28) — shown ONLY to a LearnWorlds clinician
// whose bundled grant is still live and who is inside the 30-day window. That is
// the one state where renewing costs something silently: completion stamps
// `valid_until` at the payment instant (`supersede_system_override`), so the
// remainder of year 1 is forfeited rather than carried. 10.14's review upheld
// that behaviour — carrying the remainder cannot be made to stick, because
// Stripe overwrites the period on the next sync — so the UI names it instead.
//
// Deliberately NOT shown to the lapsed population (nothing left to lose; the
// sentence would be true but pointless noise for the main renewal cohort), and
// not to the legacy variant, which has no renewal window at all.
const EARLY_RENEWAL_FORFEIT_COPY =
  "Renewing starts a new year immediately — any time left on your current year " +
  "is not carried over.";

export type BillingVariant = "org_seat" | "legacy" | "learnworlds";

/**
 * Data-driven variant selection (AC 6 — NO role branching). `source === "org_seat"`
 * wins (B2B); otherwise B2C, split on the JWT `user_origin`. `webapp_legacy` → the
 * in-product Stripe path; every other B2C origin (`learnworlds_new`, and any
 * unknown/absent origin) defaults to the LearnWorlds path — the "new AMPS learner"
 * is the default B2C destination.
 *
 * Story 15.9 (#719, AC9) SCOPES that rule rather than overturning it. This function
 * answers "which of the three billing shapes is this?", which is a question about a
 * billing row, and it still answers it from data alone. The role predicate added in
 * `SubscriptionSection` below answers the question BEFORE it — *is there a billing
 * row to have a shape at all?* An org_admin has none and can acquire none (FR48 puts
 * B2B entitlements out of Stripe entirely), so no amount of data can select a
 * truthful variant for them. The two predicates do not overlap: the role one gates
 * whether this function runs, and hands it an untouched job when it does.
 */
export function selectBillingVariant(input: {
  source: string | undefined;
  userOrigin: string | null | undefined;
}): BillingVariant {
  if (input.source === "org_seat") return "org_seat";
  if (input.userOrigin === USER_ORIGIN_WEBAPP_LEGACY) return "legacy";
  return "learnworlds";
}

// The local `isOrgLapsed` + `subscriptionPill` pair that used to live here is
// gone: both the discrimination and the copy now come from `subscriptionPill` in
// `lib/subscription-copy.ts`, which every surface reads. The card's own active
// label moved with it — the PM ruling of 2026-08-31 restores "Subscription
// active" here, reverting the half of Story 16.6's rename that produced DW-437.

/**
 * True when the current term ends within the 30-day renewal window (AC 3). A null
 * term (pre-clock / no subscription) is never "open"; an already-past term counts
 * (they need to renew), so the boundary is `validUntil - now <= 30 days`.
 */
export function isRenewalWindowOpen(
  validUntil: Date | null,
  now: Date,
): boolean {
  if (!validUntil) return false;
  return validUntil.getTime() - now.getTime() <= RENEWAL_WINDOW_MS;
}

function formatRenewalDate(validUntil: Date | null): string | null {
  if (!validUntil || Number.isNaN(validUntil.getTime())) return null;
  return validUntil.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * The contract END date from the lapse instant (Story 13.6, AC5 off-by-one).
 * `valid_until` = 13.1's lapse instant = contract_end_date + 1 day @ 00:00
 * UTC, so the displayed "ended on" date is the calendar day BEFORE — formatted
 * in UTC so a viewer west of Greenwich never sees it shift another day back.
 */
export function formatContractEndDate(validUntil: Date): string {
  const endDate = new Date(validUntil.getTime() - 24 * 60 * 60 * 1000);
  return endDate.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/**
 * True when the LearnWorlds cohort should be offered its year-2 renewal (Story
 * 10.15). TWO states qualify and the difference matters:
 *
 *   - the grant has LAPSED — and a lapsed system grant resolves with
 *     `valid_until = null` (`get_subscription_state/schemas.py`: "Null for the
 *     pre-calibration / no-subscription case"), which `isRenewalWindowOpen`
 *     reads as false. Gating on the window ALONE would therefore hide the CTA
 *     from precisely the population that needs it — the defect this story fixes;
 *   - the grant is still live but inside the 30-day window, matching the
 *     reminder marks (`send_renewal_reminders._MARKS` at 30 and 7 days).
 *
 * Outside those, no CTA: 10.14's review upheld the status quo on early-renewal
 * forfeiture, so we do not invite a renewal that silently forfeits the remainder
 * of a grant with months left on it.
 *
 * Pre-calibration is NEVER offered a renewal (FR47) — a paid-or-granted account
 * waiting on calibration has nothing to renew, and the marker arrives on an
 * INACTIVE claim, so the lapsed leg above would otherwise admit it.
 *
 * The window leg additionally requires the BUNDLED grant. `selectBillingVariant`
 * routes on `userOrigin` alone, so this predicate is also asked about a cohort
 * clinician who has ALREADY renewed in-app: in the last 30 days of that *paid*
 * year the claim is active and in-window, but there is nothing to renew and the
 * server answers 409 BILL-005. Only the bundled year resolves as
 * `manual_override`, so an active claim is offered renewal on that source alone.
 * The test is an allow-list rather than a ban on `individual_subscription`
 * because `ResolvedSubscription.source` is typed `SubscriptionSource | string` —
 * any value on the wire reaches here, and an unrecognised one must fail closed.
 * A LAPSED claim is offered renewal whatever its source: that covers both the
 * expired grant and the no-entitlement default, which also reports
 * `individual_subscription` (`common/entitlements.py`).
 */
export function isLearnWorldsRenewalOffered(
  input: {
    isActive: boolean;
    notes: string | null;
    source: string | undefined;
    validUntil: Date | null;
  },
  now: Date = new Date(),
): boolean {
  if (isPreCalibrationNotes(input.notes)) return false;
  if (!input.isActive) return true;
  if (input.source !== "manual_override") return false;
  return isRenewalWindowOpen(input.validUntil, now);
}

/**
 * The in-product checkout control, shared by both B2C variants since Story 10.15.
 * Mints a hosted Stripe Checkout session and hands the browser over to it.
 *
 * The in-flight `disabled` + `aria-busy` is the client half of the double-charge
 * mitigation, and it is no longer the only half: Story 16.7 closed
 * `seam-10.7-checkout-idempotency` server-side (an advisory lock plus a per-user
 * Stripe idempotency key, so a second mint RESUMES the in-flight session instead
 * of creating a second paid one). Keep this guard anyway — it is what stops the
 * second request being sent at all, and it is what makes the double click a
 * no-op rather than a round trip.
 *
 * `label` differs by state, not by cohort: a clinician who has never paid is
 * RESUMING an onboarding they started (Story 16.7, AC10), while everybody else
 * is renewing. The request, the error mapping and the redirect are identical.
 */
function RenewCheckoutButton({
  label = "Renew subscription",
  busyLabel = "Starting checkout…",
}: {
  label?: string;
  busyLabel?: string;
} = {}) {
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const onRenew = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiFetch<{ checkout_url: string; session_id: string }>(
        "/billing/checkout-sessions/create",
        { method: "POST", body: {} },
      );
      // Full-page redirect to the hosted Stripe Checkout (leaves the SPA).
      window.location.assign(res.checkout_url);
    } catch (err) {
      // Non-alarm inline error (Banner variant="warn"); re-enable so they can
      // retry the refusals that are actually retryable. `checkout-error.ts` is
      // what decides which sentence each refusal gets — BILL-003 and BILL-006
      // are permanent and say so.
      setError(toCheckoutErrorMessage(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div>
        <Button
          type="button"
          variant="primary"
          onClick={onRenew}
          disabled={submitting}
          aria-busy={submitting}
        >
          {submitting ? busyLabel : label}
        </Button>
      </div>
      {error ? (
        <Banner variant="warn" role="alert">
          {error}
        </Banner>
      ) : null}
    </div>
  );
}

/**
 * LearnWorlds cohort (`learnworlds_new`, and every unknown/absent B2C origin) —
 * in-product Stripe checkout for year 2 (Story 10.15, FR48 inverted). Year 1 came
 * bundled with the LearnWorlds course as a system-granted override; renewal does
 * not, because the clinician's LearnWorlds access is gone by then.
 *
 * `selectBillingVariant` also routes malformed/absent origins here, so this is
 * where an account outside the checkout cohorts lands. The server refuses those
 * with 403 BILL-003 and `checkout-error.ts` renders the permanent-refusal
 * sentence — the button is offered on the client's read of the entitlement, and
 * the server remains the authority on eligibility.
 */
function LearnWorldsVariant({
  isActive,
  notes,
  source,
  validUntil,
}: {
  isActive: boolean;
  notes: string | null;
  source: string | undefined;
  validUntil: Date | null;
}) {
  if (!isLearnWorldsRenewalOffered({ isActive, notes, source, validUntil }))
    return null;
  // `isActive` is the whole test, and it does not re-derive the window.
  //
  // The predicate above returns early for every INACTIVE shape (lapsed,
  // pre-calibration) and admits an ACTIVE one only when it is both in-window and
  // the bundled `manual_override` grant. So an active claim reaching this line is
  // necessarily the grant, and the forfeit is exactly the active case. Re-testing
  // the window or the source here would re-read the clock and let the sentence
  // and the button disagree at a midnight boundary.
  //
  // A LAPSED clinician gets the button with no sentence: there is no remainder
  // left to lose.
  const forfeitsBundledRemainder = isActive;
  return (
    <>
      {forfeitsBundledRemainder ? (
        <p className="text-sm text-[color:var(--text-secondary)]">
          {EARLY_RENEWAL_FORFEIT_COPY}
        </p>
      ) : null}
      <RenewCheckoutButton />
    </>
  );
}

/** Legacy clinician — in-product Stripe checkout (Story 10.7), then browser redirect. */
function LegacyVariant() {
  return <RenewCheckoutButton />;
}

/**
 * Screen 14b's Subscription card — stage 1, the certificate still under review.
 *
 * The CTA is DISABLED rather than absent, which is 14b's own choice and the
 * right one here: it tells a clinician who has just uploaded a certificate what
 * happens next and what unblocks it, where an empty card would leave them
 * wondering whether payment is expected of them at all. Nothing about it is a
 * client-side-only gate — the server holds the same line with 403 LEGACY-001 —
 * and the body sentence says in as many words that no money moves yet.
 */
function LegacyPendingVerificationVariant() {
  return (
    <>
      <p className="text-sm text-[color:var(--text-secondary)]">
        {LEGACY_PENDING_VERIFICATION_COPY}
      </p>
      <div>
        <Button
          type="button"
          variant="primary"
          disabled
          aria-disabled="true"
          title={LEGACY_VERIFICATION_PENDING_REASON}
        >
          {LEGACY_PENDING_VERIFICATION_CTA}
        </Button>
      </div>
    </>
  );
}

/**
 * Story 16.7 — the onboarding half of the legacy variant (W3 rows 1-4).
 *
 * `awaiting_payment` covers BOTH the never-started and the abandoned/failed
 * checkout: they are the same state, because an abandoned hosted session leaves
 * no subscription row behind, and the same button resumes either one.
 *
 * `activating` shows no CTA at all — the money has arrived and pressing checkout
 * again would answer 409 BILL-005 ("nothing to renew right now"), which is both
 * useless and alarming to somebody who has just paid.
 */
function LegacyOnboardingVariant({
  state,
  claimCreatedAt,
}: {
  state: LegacyOnboardingState;
  claimCreatedAt: Date | null;
}) {
  if (state === "awaiting_payment") {
    return (
      <>
        <p className="text-sm text-[color:var(--text-secondary)]">
          {LEGACY_AWAITING_PAYMENT_COPY}
        </p>
        <RenewCheckoutButton
          label="Resume payment"
          busyLabel="Starting payment…"
        />
      </>
    );
  }
  return <LegacyActivatingVariant activatingSince={claimCreatedAt} />;
}

/**
 * The waiting state, plus AC14's support prompt once it has outlived the
 * threshold. One `setTimeout` armed for the exact remaining interval — not a
 * poll — so the prompt appears on time without the surface re-rendering every
 * few seconds while a clinician watches it.
 *
 * The anchor is the SERVER's `claimCreatedAt` — the payment instant — not the
 * moment this component mounted. A mount-time anchor restarts on every reload,
 * and the clinician AC14 is written for (activation stuck, refreshing Profile
 * because nothing is happening) is exactly the one who reloads, so a local clock
 * would withhold the prompt from precisely that population.
 *
 * A null anchor (`org_seat` / `manual_override` — no row, no payment instant)
 * arms no timer and shows no prompt.
 */
function LegacyActivatingVariant({
  activatingSince,
}: {
  activatingSince: Date | null;
}) {
  const anchor = activatingSince ? activatingSince.getTime() : null;
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (anchor === null) return;
    const remaining = ACTIVATION_SUPPORT_PROMPT_MS - (Date.now() - anchor);
    // Already past the threshold: `now` was initialised from the clock, so the
    // prompt is showing on this very render and there is nothing to schedule.
    if (remaining <= 0) return;
    const timer = window.setTimeout(() => setNow(Date.now()), remaining);
    return () => window.clearTimeout(timer);
  }, [anchor]);

  return (
    <>
      <p className="text-sm text-[color:var(--text-secondary)]">
        {LEGACY_ACTIVATING_COPY}
      </p>
      {isSupportPromptDue(anchor, now) ? (
        <p role="status" className="text-sm text-[color:var(--text-secondary)]">
          {LEGACY_ACTIVATION_SUPPORT_COPY}
        </p>
      ) : null}
    </>
  );
}

/**
 * Organizational seat user — helper copy only, NO CTAs in ANY state (Story
 * 10.8 AC 5, held for the 13.6 lapsed variant too: org-seat users are never
 * forwarded to subscription manipulations — renewal is org-admin/CITS scope).
 * Active / pre-calibration → the standard B2B copy; lapsed → the UX 13.11
 * ended-on copy; inactive with no lapse instant → the read-only copy without
 * a date sentence.
 */
function OrgSeatVariant({
  isActive,
  notes,
  validUntil,
  lapsed,
}: {
  isActive: boolean;
  notes: string | null;
  validUntil: Date | null;
  /**
   * `pillState === "org_lapsed"`, decided by the shared mapper. Passed in rather
   * than re-derived so the "ended on {date}" sentence and the `blocked` pill can
   * never disagree at a midnight boundary — they now read one clock, once.
   */
  lapsed: boolean;
}) {
  let copy = B2B_HELPER_COPY;
  if (!isActive && !isPreCalibrationNotes(notes)) {
    copy = lapsed
      ? `Your organization's contract ended on ${formatContractEndDate(
          validUntil as Date,
        )}. ${B2B_READ_ONLY_COPY}`
      : B2B_READ_ONLY_COPY;
  }
  return <p className="text-sm text-[color:var(--text-secondary)]">{copy}</p>;
}

// gh-626 — the terminal, non-alarm copy for a settled query error. Tertiary
// text on the standard card (no warn Banner): "couldn't load" is an unknown
// state, not a billing alarm, and writes stay ungated (AC7).
const LOAD_ERROR_COPY =
  "Couldn't load subscription details. Refresh the page to try again.";

export function SubscriptionSection() {
  const { user, hasRole, isLegacyGated } = useAuth();
  // keepResolvedOnError (gh-622 PR review): a failed background refetch must
  // not collapse an on-screen resolved card back to `loading` — the cached
  // payload keeps rendering. `loadFailed` is first-fetch-only (data-less
  // settled error), so the pair can never show the error copy over good data.
  //
  // Story 16.7 (D2, boundary 14) — a legacy clinician waiting on activation
  // polls instead of sitting on the 60s `staleTime`. The transition this catches
  // is `no_entitlement` → `Pre-calibration`, i.e. the webhook writing the
  // subscription row, which is what turns "Resume payment" into the waiting
  // state; the account-enabled half rides the existing token rotation, not this
  // query (a paid pre-calibration clinician's ENTITLEMENT stays inactive by
  // FR47, so the flip is invisible here). Scoped to `isLegacyGated`, so nobody
  // else's Profile gains a poll.
  const subscription = useSubscriptionState({
    keepResolvedOnError: true,
    refetchInterval: isLegacyGated ? 15_000 : undefined,
  });
  const loadFailed = useSubscriptionStateError();

  // Story 15.9 (#719) — visibility gate, and the prior question `selectBillingVariant`
  // above is deliberately not asked to answer. A role with no clinical entitlement has
  // no billing row and can acquire none, so every variant this section could pick is a
  // false statement to them; an org_admin's own entitlement resolves to the B2C
  // "Subscription lapsed" pill while their dashboard reports the organization's
  // contract active. A POSITIVE allow-list on ROLE, never on the resolved payload: a
  // lapsed B2C clinician resolves the identical `no_entitlement` shape and must keep
  // this whole section (Story 10.5's surface).
  // Both hooks above stay above this return, as React's rules of hooks require — so
  // the billing query is still issued for a role that hides the answer. That is a
  // wasted round trip rather than a leak (the endpoint is authenticated as the caller
  // and returns only the caller's own entitlement), and widening the six-consumer hook
  // with an `enabled` option to save it is blast radius bought for one request.
  if (!hasRole(...CLINICIAN_ROLES)) return null;

  const header = (
    <div>
      <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">
        Subscription
      </h2>
      <p className="text-sm text-[color:var(--text-tertiary)]">
        Your plan status and renewal options.
      </p>
    </div>
  );

  // Unknown state (loading / errored / offline): show a neutral, non-alarm
  // placeholder rather than guessing a variant before `source` is known. The
  // guard also absorbs a resolved-but-malformed payload (no boolean `isActive`).
  // gh-626 — a SETTLED query error renders a terminal "couldn't load" card
  // instead of an endless "Loading subscription…"; the union itself still maps
  // error→loading, so `isSubscriptionLapsed` stays false and writes stay open.
  if (!isResolvedSubscription(subscription)) {
    return (
      <section className="flex flex-col gap-4">
        {header}
        <Card>
          <p
            className="text-sm text-[color:var(--text-tertiary)]"
            role={loadFailed ? "status" : undefined}
          >
            {loadFailed ? LOAD_ERROR_COPY : "Loading subscription…"}
          </p>
        </Card>
      </section>
    );
  }

  // Resolved — active | lapsed both carry the entitlement facts (fully typed here).
  const { isActive, notes, validUntil, source, claimCreatedAt } = subscription;

  // ONE call to the shared mapper answers both questions this card used to ask
  // separately: which pill to render, and which body variant. Story 16.7's
  // onboarding moments win inside the mapper, because the billing answer is
  // wrong for both of them — an unpaid legacy clinician resolves the
  // `no_entitlement` default and would be pilled "Subscription lapsed", naming a
  // subscription they have never had.
  const pill = subscriptionPill({
    subscription,
    isLegacyGated,
    certVerified: Boolean(user?.certVerified),
  });
  const legacyOnboarding: LegacyOnboardingState | null =
    isLegacyOnboardingPillState(pill.state) ? pill.state : null;
  // Screen 14b's stage-1 moment, READ from the same one answer the pill came
  // from rather than re-tested from `isLegacyGated && !certVerified` here. The
  // W3 no-second-derivation rule is the reason, and DW-458 is what it cost the
  // last time this card and its neighbour derived one question twice.
  const pendingVerification = pill.state === "pending_verification";
  // The BILLING state with the onboarding overlay stripped off — the org-seat
  // body copy is a statement about the contract, so it must not change just
  // because the reader happens to be mid-onboarding. It is the shared answer for
  // BOTH body branches that ask a billing question: the org-seat lapse sentence
  // and the legacy pre-calibration one.
  const billingPillState = selectSubscriptionPillState({ subscription });
  const variant = selectBillingVariant({
    source,
    userOrigin: user?.userOrigin,
  });
  // gh-641: the pill's "Activating after calibration" signal, narrowed to the
  // PAID Stripe path — a manual-override grantee (FR48) carries the same notes
  // marker with no subscription behind it, so for them the body sentence would
  // promise a subscription that will never begin (and the server would still
  // allow their checkout: 409 BILL-005 keys on subscription rows, which an
  // override grantee lacks).
  //
  // PR #741 review: the signal is READ from `billingPillState` above, not
  // re-derived from `isPreCalibrationSubscription` three lines below the call that
  // already answered it. Two derivations of one question on one card is exactly
  // what DW-458 cost, and the W3 no-second-derivation rule says a state-bearing
  // label keys on the shared discriminator. The `source` term stays explicit
  // BESIDE it — it is a deliberate narrowing of the shared answer, not a
  // restatement of it, and writing it out is what keeps the divergence visible.
  const pendingCalibration =
    billingPillState === "pending_calibration" &&
    source === "individual_subscription";
  // Only an ACTIVE B2C plan has a real forward renewal date. A lapsed state
  // carries `valid_until = current_period_end` — a stale/past instant that would
  // read as an upcoming renewal — and the org_seat B2B card must show helper copy
  // ONLY (AC 5). Gate the line on both, so neither leaks the date.
  const renewalDate =
    isActive && variant !== "org_seat" ? formatRenewalDate(validUntil) : null;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        {header}
        <Badge variant={pill.variant}>{pill.text}</Badge>
      </div>
      <Card>
        <div className="flex flex-col gap-4">
          {renewalDate ? (
            <p className="text-sm text-[color:var(--text-secondary)]">
              <strong>Renewal date:</strong>{" "}
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{renewalDate}</span>
            </p>
          ) : null}

          {variant === "org_seat" ? (
            <OrgSeatVariant
              isActive={isActive}
              notes={notes}
              validUntil={validUntil}
              lapsed={billingPillState === "org_lapsed"}
            />
          ) : null}
          {variant === "legacy" ? (
            // Screen 14b's stage-1 branch sits ABOVE Story 16.7's, which sits
            // above 16.6's, and each order is deliberate.
            //
            // 14b first, because every branch below it describes a clinician
            // whose certificate CITS has already approved, and stage 1 resolves
            // the same `no_entitlement` default as the abandoned-checkout case:
            // reaching 16.7's row would offer a checkout FR39 refuses. (The
            // branch cannot be misplaced either — `isLegacyPendingVerification`
            // requires the individual-subscription claim, so it never fires on
            // the org-seat card, which this `variant` test has already excluded.)
            //
            // Then 16.7 above 16.6, because those two states share the
            // `Pre-calibration` marker and are told apart only by whether the
            // account is enabled. A clinician who has paid but is NOT yet
            // enabled would otherwise be told "your subscription will begin once
            // you complete calibration" — true eventually, and useless right
            // now, because calibration is exactly what they cannot reach until
            // the activation event lands.
            pendingVerification ? (
              <LegacyPendingVerificationVariant />
            ) : legacyOnboarding ? (
              <LegacyOnboardingVariant
                state={legacyOnboarding}
                claimCreatedAt={claimCreatedAt}
              />
            ) : pendingCalibration ? (
              <p className="text-sm text-[color:var(--text-secondary)]">
                {LEGACY_PENDING_CALIBRATION_COPY}
              </p>
            ) : (
              <LegacyVariant />
            )
          ) : null}
          {variant === "learnworlds" ? (
            <LearnWorldsVariant
              isActive={isActive}
              notes={notes}
              source={source}
              validUntil={validUntil}
            />
          ) : null}
        </div>
      </Card>
    </section>
  );
}
